import { bytesToBase64, base64ToBytes } from './utils';

/**
 * Gera um token de compartilhamento aleatório (12 caracteres alfanuméricos)
 */
export function generateShareToken(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  let token = '';
  for (let i = 0; i < 12; i++) {
    token += chars[bytes[i] % chars.length];
  }
  return token;
}

/**
 * Deriva uma chave de criptografia a partir de um token de compartilhamento
 * Usa PBKDF2 para derivação rápida (o token já tem entropia suficiente)
 */
async function deriveKeyFromToken(token: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const tokenBytes = encoder.encode(token);

  // Importar token como chave
  const baseKey = await crypto.subtle.importKey(
    'raw',
    tokenBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );

  // Derivar 256 bits usando salt fixo (token já é aleatório)
  const salt = encoder.encode('oneckpass-share-v1');
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: 10000,
      hash: 'SHA-256',
    },
    baseKey,
    256
  );

  return new Uint8Array(derivedBits);
}

/**
 * Criptografa a chave do cofre (encryptionKey) com um token de compartilhamento
 * Retorna a chave criptografada em base64
 */
export async function encryptKeyWithToken(
  encryptionKey: Uint8Array,
  shareToken: string
): Promise<string> {
  const derivedKey = await deriveKeyFromToken(shareToken);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    derivedKey.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encryptionKey.buffer as ArrayBuffer
  );

  // Formato: iv (12 bytes) + ciphertext
  const result = new Uint8Array(iv.length + ciphertext.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(ciphertext), iv.length);

  return bytesToBase64(result);
}

/**
 * Descriptografa a chave do cofre usando um token de compartilhamento
 * Retorna a encryptionKey original
 */
export async function decryptKeyWithToken(
  encryptedKey: string,
  shareToken: string
): Promise<Uint8Array> {
  const derivedKey = await deriveKeyFromToken(shareToken);
  const data = base64ToBytes(encryptedKey);

  // Extrair IV (primeiros 12 bytes) e ciphertext
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    derivedKey.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    cryptoKey,
    ciphertext.buffer as ArrayBuffer
  );

  return new Uint8Array(decrypted);
}

/**
 * Exporta a encryptionKey para base64 (para armazenamento temporário)
 */
export function exportKey(key: Uint8Array): string {
  return bytesToBase64(key);
}

/**
 * Importa a encryptionKey de base64
 */
export function importKey(keyBase64: string): Uint8Array {
  return base64ToBytes(keyBase64);
}
